import { useState, useEffect } from "react"
import Cookies from "js-cookie"
import cc from "classcat"
import { handleScrollToAnchor } from "utils/browser"
import useTranslations from "hooks/useTranslations"

import H2 from "components/atoms/H2"
import PricingTier from "components/molecules/PricingTier"
import OrderModal from "components/OrderModal"

import {
  stripeSKUBaseOneCase,
  stripeSKUBaseSubscription,
  stripeSKUParentalOneCase,
  stripeSKUParentalSubscription,
  stripeSKUInfluencerOneCase,
  stripeSKUInfluencerOneCasePartner,
  stripeSKUInfluencerSubscription,
  stripeSKUInfluencerSubscriptionPartner,
} from "utils/env"

const TwoTierPricing = ({ tiers = [], title, className }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [partner, setPartner] = useState(false)

  const {
    pricingTierInfluencerOneCaseName,
    pricingTierInfluencerOneCasePrice,
    pricingTierInfluencerOneCasePricePartner,
    pricingTierInfluencerOneCaseDuration,
    pricingTierInfluencerOneCaseDescription,
    pricingTierInfluencerSubscriptionName,
    pricingTierInfluencerSubscriptionPrice,
    pricingTierInfluencerSubscriptionPricePartner,
    pricingTierInfluencerSubscriptionDuration,
    pricingTierInfluencerSubscriptionDescription,
    pricingTierBaseOneCaseName,
    pricingTierBaseOneCasePrice,
    pricingTierBaseOneCaseDuration,
    pricingTierBaseOneCaseDescription,
    pricingTierBaseSubscriptionName,
    pricingTierBaseSubscriptionPrice,
    pricingTierBaseSubscriptionDuration,
    pricingTierBaseSubscriptionDescription,
    pricingTierParentalOneCaseName,
    pricingTierParentalOneCasePrice,
    pricingTierParentalOneCaseDuration,
    pricingTierParentalOneCaseDescription,
    pricingTierParentalSubscriptionName,
    pricingTierParentalSubscriptionPrice,
    pricingTierParentalSubscriptionDuration,
    pricingTierParentalSubscriptionDescription,
    tierFeature1,
    tierFeature2,
    tierFeature3,
    tierFeature4,
    tierFeature5,
  } = useTranslations()

  const [pricingData, setPricingData] = useState(null)

  useEffect(() => {
    const partnerCookie = Cookies.get("partner")
    if (partnerCookie) {
      setPartner(partnerCookie)
    }
    // const params = new URLSearchParams(location.search)
    // if (params.has("modal")) {
    //   setModalIsOpen(true)
    // }
  }, [])

  const openModal = () => {
    handleScrollToAnchor()
    setModalIsOpen(true)
  }

  useEffect(() => {
    if (pricingData) openModal()
  }, [pricingData])

  const pricingTiers = {
    pricingTierInfluencerOneCase: {
      name: pricingTierInfluencerOneCaseName,
      fullPrice: pricingTierInfluencerOneCasePrice,
      partnerPrice: pricingTierInfluencerOneCasePricePartner,
      duration: pricingTierInfluencerOneCaseDuration,
      features: [tierFeature1, tierFeature2, tierFeature4, tierFeature5],
      pricingMode: "payment",
      sku: partner
        ? stripeSKUInfluencerOneCasePartner
        : stripeSKUInfluencerOneCase,
      description: pricingTierInfluencerOneCaseDescription,
      partner,
    },
    pricingTierInfluencerSubscription: {
      name: pricingTierInfluencerSubscriptionName,
      partnerPrice: pricingTierInfluencerSubscriptionPricePartner,
      fullPrice: pricingTierInfluencerSubscriptionPrice,
      duration: pricingTierInfluencerSubscriptionDuration,
      features: [tierFeature1, tierFeature3, tierFeature4, tierFeature5],
      pricingMode: "subscription",
      sku: partner
        ? stripeSKUInfluencerSubscriptionPartner
        : stripeSKUInfluencerSubscription,
      description: pricingTierInfluencerSubscriptionDescription,
      partner,
    },
    pricingTierBaseOneCase: {
      name: pricingTierBaseOneCaseName,
      fullPrice: pricingTierBaseOneCasePrice,
      partnerPrice: pricingTierBaseOneCasePrice,
      duration: pricingTierBaseOneCaseDuration,
      features: [tierFeature1, tierFeature2],
      pricingMode: "payment",
      sku: stripeSKUBaseOneCase,
      description: pricingTierBaseOneCaseDescription,
      partner,
    },
    pricingTierBaseSubscription: {
      name: pricingTierBaseSubscriptionName,
      fullPrice: pricingTierBaseSubscriptionPrice,
      partnerPrice: pricingTierBaseSubscriptionPrice,
      duration: pricingTierBaseSubscriptionDuration,
      features: [tierFeature1, tierFeature3],
      pricingMode: "subscription",
      sku: stripeSKUBaseSubscription,
      description: pricingTierBaseSubscriptionDescription,
      partner,
    },
    pricingTierParentalOneCase: {
      name: pricingTierParentalOneCaseName,
      fullPrice: pricingTierParentalOneCasePrice,
      partnerPrice: pricingTierParentalOneCasePrice,
      duration: pricingTierParentalOneCaseDuration,
      features: [tierFeature1, tierFeature2],
      pricingMode: "payment",
      sku: stripeSKUParentalOneCase,
      description: pricingTierParentalOneCaseDescription,
      partner,
    },
    pricingTierParentalSubscription: {
      name: pricingTierParentalSubscriptionName,
      fullPrice: pricingTierParentalSubscriptionPrice,
      partnerPrice: pricingTierParentalSubscriptionPrice,
      duration: pricingTierParentalSubscriptionDuration,
      features: [tierFeature1, tierFeature3],
      pricingMode: "subscription",
      sku: stripeSKUParentalSubscription,
      description: pricingTierParentalSubscriptionDescription,
      partner,
    },
  }

  const handleClick = (tier) => () => {
    setPricingData(pricingTiers[tier])
  }

  return (
    <div className={cc(["relative mb-0 py-12 pb-8 md:py-16", className])}>
      <div id="Order">
        <div className="relative mt-16 sm:mt-16 lg:mt-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {title && (
              <div className="mx-auto mb-8 max-w-2xl text-center lg:max-w-4xl">
                <H2 textAlign="center">{title}</H2>
              </div>
            )}
            <div className="mx-auto max-w-lg space-y-10 lg:grid lg:max-w-5xl lg:grid-cols-4 lg:gap-10 lg:space-y-0">
              {tiers.map((tier) => (
                <div
                  className={cc([
                    "col-span-2 flex",
                    tiers.length > 1 ? "" : "col-start-2",
                  ])}
                >
                  <PricingTier
                    onClick={handleClick(tier)}
                    key={tier}
                    {...pricingTiers[tier]}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <OrderModal
        modalIsOpen={modalIsOpen}
        setModalIsOpen={setModalIsOpen}
        // partner={partner}
        {...pricingData}
      />
    </div>
  )
}

export default TwoTierPricing
